import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { FarmState } from '../domain/model';
import { normalizeState } from '../domain/normalize';
import { SyncConflictError, type AppMode, type AppUser, type FarmBackend, type FarmMembership, type FarmRole, type RemoteSnapshot } from './types';

const PUBLIC_URL = 'https://goorcjsshqabwpzkmtay.supabase.co';
const PUBLIC_KEY = 'sb_publishable__gwkhVXiwGekmiRudGaVng_Tahp8p_l';

export function loginEmailFromUsername(username: string): string {
  const normalized = username.trim().toLowerCase();
  if (!/^[a-z0-9][a-z0-9._-]{2,31}$/.test(normalized)) throw new Error('Escribe un usuario válido, sin espacios (3 a 32 caracteres).');
  return `${normalized}@usuarios.xtremepork.app`;
}

export function authErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String((error as { message?: string })?.message || error || '');
  if (/invalid login credentials/i.test(message)) return 'El usuario o la contraseña no coinciden.';
  if (/email not confirmed/i.test(message)) return 'La cuenta todavía no está confirmada.';
  if (/fetch|network|timeout|offline/i.test(message)) return 'No fue posible conectar con Supabase. Los cambios locales se conservan.';
  return message || 'No fue posible completar la operación.';
}

export function isNetworkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String((error as { message?: string })?.message || error || '');
  return /fetch|network|timeout|offline|conexi[oó]n/i.test(message);
}

export function createSupabaseBackend(mode: AppMode, client?: SupabaseClient): FarmBackend {
  const sb = client || createClient(import.meta.env.VITE_SUPABASE_URL || PUBLIC_URL, import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || PUBLIC_KEY, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, ...(mode === 'preview' ? { storageKey: 'xp_react_preview_auth_v1' } : {}) },
  });
  const writable = mode === 'production';
  const requireWrite = () => { if (!writable) throw new Error('El entorno de pruebas no permite escrituras remotas.'); };
  const readRow = (row: { datos: unknown; updated_at: string }): RemoteSnapshot => ({ state: normalizeState(row.datos), revision: row.updated_at });
  return {
    writable,
    async getUser() {
      const { data, error } = await sb.auth.getUser();
      if (error && error.name !== 'AuthSessionMissingError') throw error;
      return data.user ? { id: data.user.id, email: data.user.email } : null;
    },
    async getCachedUser() {
      const { data, error } = await sb.auth.getSession();
      if (error) throw error;
      return data.session?.user ? { id: data.session.user.id, email: data.session.user.email } : null;
    },
    async signIn(username, password) {
      const { data, error } = await sb.auth.signInWithPassword({ email: loginEmailFromUsername(username), password });
      if (error) throw error;
      if (!data.user) throw new Error('No fue posible validar la sesión.');
      return { id: data.user.id, email: data.user.email };
    },
    async signOut() { const { error } = await sb.auth.signOut({ scope: 'local' }); if (error) throw error; },
    onAuthChange(callback) {
      const { data } = sb.auth.onAuthStateChange((_event, session) => {
        const user: AppUser | null = session?.user ? { id: session.user.id, email: session.user.email } : null;
        // Leave the auth lock before any membership/snapshot requests are triggered.
        setTimeout(() => callback(user), 0);
      });
      return () => data.subscription.unsubscribe();
    },
    async getMembership(userId) {
      const { data, error } = await sb.from('miembros_finca').select('finca_id,rol,fincas(nombre)').eq('usuario_id', userId).order('created_at').limit(1).maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const roles = ['propietario', 'administrador', 'operador', 'consulta'];
      if (!roles.includes(data.rol)) throw new Error('El rol de esta cuenta no está autorizado.');
      const farm = data.fincas as unknown as { nombre?: string } | { nombre?: string }[] | null;
      return { id: data.finca_id, nombre: (Array.isArray(farm) ? farm[0]?.nombre : farm?.nombre) || 'Xtreme Pork', role: data.rol as FarmRole };
    },
    async getSnapshot(farmId) {
      const { data, error } = await sb.from('estado_aplicacion').select('datos,updated_at').eq('finca_id', farmId).maybeSingle();
      if (error) throw error;
      return data ? readRow(data) : null;
    },
    async createFarm(userId, name) {
      requireWrite();
      const { data, error } = await sb.from('fincas').insert({ nombre: name.trim() || 'Xtreme Pork', creada_por: userId }).select('id,nombre').single();
      if (error) throw error;
      return { id: data.id, nombre: data.nombre, role: 'propietario' } satisfies FarmMembership;
    },
    async writeSnapshot(farmId, userId, state: FarmState, expectedRevision) {
      requireWrite();
      const row = { finca_id: farmId, datos: state, version: 1, actualizado_por: userId };
      const request = expectedRevision === null
        ? sb.from('estado_aplicacion').insert(row)
        : sb.from('estado_aplicacion').update(row).eq('finca_id', farmId).eq('updated_at', expectedRevision);
      const { data, error } = await request.select('datos,updated_at').maybeSingle();
      if (error?.code === '23505' || (!error && !data)) throw new SyncConflictError();
      if (error) throw error;
      if (!data) throw new SyncConflictError();
      return readRow(data);
    },
  };
}
